import {DataStatus, Form, FormField} from 'platform/components';
import {Grid, GridItem, VStack} from 'platform/foundation';

import {useGetManufacturersQuery} from '@dms/api/core';
import {useGetSuppliersQuery} from '@dms/api/warehouse';
import {i18n} from '@dms/i18n';
import {
  getOptionsFromManufacturers,
  getOptionsFromSuppliers,
  getSearchTypeOptions,
} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {FormToDataGridConnectorProps} from 'features/eag-grid';

type ReplaceArticlePricelistTabExternalFiltersForm = Partial<{
  catalogNumber: string;
  searchType: string;
  name: string;
  manufacturerId: string;
  supplierId: string;
}>;

type ReplaceArticlePricelistTabExternalFiltersProps = FormToDataGridConnectorProps & TestIdProps;

export function ReplaceArticlePricelistTabExternalFilters(
  props: ReplaceArticlePricelistTabExternalFiltersProps
) {
  const {
    data: manufacturers,
    isLoading: isManufacturersLoading,
    isError: isManufacturersError,
  } = useGetManufacturersQuery();

  const {
    data: suppliers,
    isLoading: isSuppliersLoading,
    isError: isSuppliersError,
  } = useGetSuppliersQuery();

  const isLoading = isManufacturersLoading || isSuppliersLoading;
  const isError = isManufacturersError || isSuppliersError;

  const searchTypeOptions = getSearchTypeOptions();
  const defaultSearchType = searchTypeOptions[0].value;

  const rawSearchType = props.values.searchType;

  const values = {
    ...props.values,
    searchType: typeof rawSearchType === 'string' ? rawSearchType : defaultSearchType,
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <Form<ReplaceArticlePricelistTabExternalFiltersForm>
        values={values}
        defaultValues={{}}
        onChange={props.onChange}
      >
        {(control) => (
          <VStack spacing={4}>
            <Grid columns={3} align="flex-end">
              <GridItem span={2}>
                <FormField
                  control={control}
                  name="catalogNumber"
                  type="text"
                  label={i18n.t('entity.warehouse.labels.catalogueNumber')}
                  data-testid={suffixTestId('catalogueNumber', props)}
                />
              </GridItem>
              <GridItem span={1}>
                <FormField
                  control={control}
                  name="searchType"
                  type="choice"
                  options={searchTypeOptions}
                  isNotClearable
                  data-testid={suffixTestId('filteringMethod', props)}
                />
              </GridItem>
            </Grid>
            <FormField
              control={control}
              name="name"
              type="text"
              label={i18n.t('entity.warehouse.labels.name')}
              data-testid={suffixTestId('name', props)}
            />
            <FormField
              control={control}
              name="manufacturerId"
              type="multiChoice"
              label={i18n.t('entity.warehouse.labels.manufacturer')}
              options={getOptionsFromManufacturers(manufacturers)}
              data-testid={suffixTestId('manufacturer', props)}
            />
            <FormField
              control={control}
              name="supplierId"
              type="multiChoice"
              label={i18n.t('entity.warehouse.labels.supplier')}
              options={getOptionsFromSuppliers(suppliers)}
              data-testid={suffixTestId('supplier', props)}
            />
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}
